import React from "react";
import { useNavigate } from 'react-router-dom';
import { setNotificationAsRead } from "../../api/notifications";

// mui
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

// icons
import CloseIcon from '@mui/icons-material/Close';

// type
type NotificationCardProps = {
    notificationId: number,
    notificationTime: string,
    isReaded: boolean,
    title: string | null,
    message: string,
    link: string | null,
    handleDeleteNotification: (notificationId: number, isReaded: boolean) => void,
}

export default function NotificationCard(props: NotificationCardProps) {
    const { notificationId, notificationTime, isReaded, title, message, link, handleDeleteNotification } = props;
    const navigate = useNavigate();

    const cardTitle = (<>
        <Typography
            component="span"
            gutterBottom
            paragraph
            sx={{display: "block", mb: 1, lineHeight: 1}}
        >
            {notificationTime}
        </Typography>
    </>);

    const information = (<>
        {title && (
            <Typography
                component="span"
                gutterBottom
                paragraph
                sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    '&::before': {
                        content: '""',
                        display: isReaded ? "none" : "inline-block",
                        width: 16,
                        height: 16,
                        mr: 0.5,
                        borderRadius: "50%",
                        backgroundColor: "#FF505F",
                    }
                }}
            >
                {title}
            </Typography>
        )}
        <Typography
            component="span"
            gutterBottom
            paragraph
            sx={{
                display: "block",
                mb: 0,
                fontSize: title ? 14 : 16,
                color: title ? "#007DFF" : "#4CAF50",
            }}
        >
            {message}
        </Typography>
    </>);

    const action = (
        <IconButton
            onClick={(e) => {
                e.stopPropagation();
                handleDeleteNotification(notificationId, isReaded);
            }}
            aria-label="delete"
        >
            <CloseIcon />
        </IconButton>
    );
    const cardContent = (
        <CardHeader
            action={action}
            title={cardTitle}
            subheader={information}
            sx={{
                flexWrap: { mb: "wrap", tb: "nowrap" },
                alignItems: { mb: "start", tb: "center" },
                px: { mb: 2, tb: 3 },
                '& .MuiCardHeader-content': {
                    width: { mb: "calc(100% - 88px)", tb: "unset" },
                    marginBottom: { mb: 2, tb: 0 },

                    '& .MuiCardHeader-title': {
                        marginBottom: 1.2,
                        '& .MuiTypography-root': {
                            fontSize: 14,
                            color: (theme) => theme.palette.grey[500],
                        }
                    },

                    '& .MuiCardHeader-subheader': {
                        color: (theme) => theme.palette.common.black,
                        '& .MuiTypography-root': {
                            fontWeight: isReaded ? 400 : 700,
                            lineHeight: 1,
                        }
                    }
                },
                '& .MuiCardHeader-action': {
                    alignSelf: "inherit",
                    mx: { mb: "auto", tb: 0 },
                    my: 0,
                }
            }}
        />
    );

    const handleClickNotification = async (notificationId: number, link: string, isReaded: boolean) => {
        if (isReaded) {
            navigate(link);
        } else {
            const result = await setNotificationAsRead(notificationId);
            if (result === "success") {
                navigate(link);
            }
        }
    }

    return (
        <Card sx={{
            width: "calc(100% - 16px)",
            maxWidth: 980,
            mx: "auto",
            borderRadius: 0,
            boxShadow: "unset",
            '& > a': {
                color: (theme) => theme.palette.grey[900],
                textDecoration: "none",
            }
        }}>
            {link ? (
                <Link
                    onClick={() => handleClickNotification(notificationId, link, isReaded)}
                    sx={{
                        display: "block",
                        "&:hover": {
                            cursor: "pointer",
                            backgroundColor: "#f6fafd",
                        }
                    }}
                >
                    {cardContent}
                </Link>
            ) : cardContent}
        </Card>
    );
}
